import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Hash, ChevronLeft, Calendar, Sun, Moon, Star, Loader2, ArrowRight, TrendingUp, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/components/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const CycleCard = ({ title, number, theme, description, icon: Icon, isHighlighted }) => (
  <div className={`
    p-6 rounded-2xl border-2 transition-all duration-300
    ${isHighlighted 
      ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-300 shadow-lg' 
      : 'bg-white border-gray-200 hover:border-emerald-200'}
  `}>
    <div className="flex items-start gap-4">
      <div className={`
        w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
        ${isHighlighted ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : 'bg-gray-100'}
      `}>
        <Icon className={`h-6 w-6 ${isHighlighted ? 'text-white' : 'text-gray-600'}`} />
      </div>
      
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          <span className={`text-3xl font-bold ${isHighlighted ? 'text-emerald-700' : 'text-gray-800'}`}>
            {number}
          </span>
          <span className="text-sm font-medium text-gray-500">{title}</span>
        </div>
        
        {theme && (
          <h3 className={`font-heading text-lg font-semibold mb-1 ${isHighlighted ? 'text-emerald-800' : 'text-gray-700'}`}>
            {theme}
          </h3>
        )}
        
        {description && (
          <p className="text-sm text-gray-600">{description}</p>
        )}
      </div>
    </div>
  </div>
);

const NumerologiaCyclesPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [birthDate, setBirthDate] = useState('');
  const [cycles, setCycles] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [interpretation, setInterpretation] = useState(null);
  const [loadingInterpretation, setLoadingInterpretation] = useState(false);
  const [profilePreFilled, setProfilePreFilled] = useState(false);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  // Auto-fill from user profile
  useEffect(() => {
    if (isAuthenticated && user && !profilePreFilled && user.birth_date) {
      setBirthDate(user.birth_date);
      setProfilePreFilled(true);
      toast.success('Date de naissance pré-remplie depuis votre profil !');
    }
  }, [isAuthenticated, user, profilePreFilled]);

  const handleCalculate = async (e) => {
    e.preventDefault();
    
    if (!birthDate) {
      toast.error('Veuillez entrer votre date de naissance');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/cycles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          birth_date: birthDate,
          target_year: currentYear,
          target_month: currentMonth
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCycles(data.cycles);
        toast.success('Cycles calculés !');
        
        // Also fetch forecast
        fetchForecast();
      } else {
        toast.error('Erreur lors du calcul');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const fetchForecast = async () => {
    setLoadingForecast(true);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/cycles/forecast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          birth_date: birthDate,
          target_year: currentYear
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setForecast(data.forecast);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingForecast(false);
    }
  };

  const getAIInterpretation = async () => {
    if (!cycles) return;
    
    setLoadingInterpretation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/cycles/interpret`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cycles_data: cycles,
          first_name: null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setInterpretation(data.interpretation);
        toast.success('Interprétation générée !');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de l\'interprétation');
    } finally {
      setLoadingInterpretation(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-emerald-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate('/numerologia')}
            className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-emerald-600" />
            <span className="font-heading text-lg font-bold text-emerald-700">
              Cycles & Météo Personnelle
            </span>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Form */}
          {!cycles && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-md mx-auto"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-8 w-8 text-white" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                  Votre météo numérologique
                </h1>
                <p className="text-gray-600">
                  Découvrez les énergies de votre année, mois et jour
                </p>
              </div>
              
              <form onSubmit={handleCalculate} className="space-y-4">
                <div>
                  <Label htmlFor="birthDate">Date de naissance</Label>
                  <Input
                    id="birthDate"
                    type="date"
                    data-testid="cycles-birthdate"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <Button
                  type="submit"
                  data-testid="calculate-cycles-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-6"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Calcul...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="mr-2 h-5 w-5" />
                      Voir mes cycles
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          )}

          {/* Results */}
          {cycles && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-8"
            >
              {/* Current Cycles */}
              <div>
                <h2 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Sun className="h-5 w-5 text-emerald-600" />
                  Vos vibrations actuelles
                </h2>
                
                <div className="grid gap-4">
                  <CycleCard
                    title="Année personnelle"
                    number={cycles.personal_year?.number}
                    theme={cycles.personal_year?.meaning?.theme}
                    description={cycles.personal_year?.meaning?.description}
                    icon={Sun}
                    isHighlighted={true}
                  />
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <CycleCard
                      title="Mois personnel"
                      number={cycles.personal_month?.number}
                      theme={`Vibration du mois de ${MONTH_NAMES[currentMonth - 1]}`}
                      icon={Moon}
                      isHighlighted={false}
                    />
                    
                    <CycleCard
                      title="Jour personnel"
                      number={cycles.personal_day?.number}
                      theme="Énergie d'aujourd'hui"
                      icon={Star}
                      isHighlighted={false}
                    />
                  </div>
                </div>
              </div>

              {/* Advice */}
              {cycles.personal_year?.meaning && (
                <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-200">
                  <h3 className="font-heading text-lg font-semibold text-emerald-800 mb-3">
                    Conseil pour votre année {cycles.personal_year.number}
                  </h3>
                  <p className="text-emerald-700 mb-4">{cycles.personal_year.meaning.advice}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {cycles.personal_year.meaning.focus?.map((f, i) => (
                      <span key={i} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 9-Year Cycle Overview */}
              {cycles.cycle_overview && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-emerald-600" />
                    Votre cycle de 9 ans
                  </h2>
                  
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {cycles.cycle_overview.map((year) => (
                      <div
                        key={year.year}
                        className={`
                          flex-shrink-0 p-4 rounded-xl text-center min-w-[100px] transition-all
                          ${year.is_current 
                            ? 'bg-emerald-600 text-white shadow-lg' 
                            : 'bg-white border border-gray-200 hover:border-emerald-200'}
                        `}
                      >
                        <p className={`text-sm ${year.is_current ? 'text-emerald-100' : 'text-gray-500'}`}>
                          {year.year}
                        </p>
                        <p className={`text-2xl font-bold ${year.is_current ? 'text-white' : 'text-gray-800'}`}>
                          {year.personal_year}
                        </p>
                        <p className={`text-xs mt-1 ${year.is_current ? 'text-emerald-100' : 'text-gray-500'}`}>
                          {year.theme?.split(' ')[0]}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly Forecast */}
              {forecast && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-emerald-600" />
                    Prévisions mensuelles {currentYear}
                  </h2>
                  
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {forecast.map((month) => (
                      <div
                        key={month.month}
                        className={`
                          p-4 rounded-xl text-center transition-all
                          ${month.month === currentMonth 
                            ? 'bg-emerald-600 text-white shadow-lg' 
                            : 'bg-white border border-gray-200 hover:border-emerald-200'}
                        `}
                      >
                        <p className={`text-xs ${month.month === currentMonth ? 'text-emerald-100' : 'text-gray-500'}`}>
                          {month.month_name.slice(0, 3)}
                        </p>
                        <p className={`text-xl font-bold ${month.month === currentMonth ? 'text-white' : 'text-gray-800'}`}>
                          {month.personal_month}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Interpretation */}
              <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <h3 className="font-heading text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-600" />
                  Interprétation IA personnalisée
                </h3>
                
                {interpretation ? (
                  <div className="prose prose-sm max-w-none text-gray-700">
                    <ReactMarkdown>{interpretation}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500 text-sm mb-4">
                      Obtenez une analyse personnalisée de vos cycles par l'IA
                    </p>
                    <Button
                      onClick={getAIInterpretation}
                      disabled={loadingInterpretation}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                      data-testid="get-cycles-interpretation-btn"
                    >
                      {loadingInterpretation ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Analyse en cours...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-5 w-5" />
                          Obtenir l'interprétation IA
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={() => { setCycles(null); setForecast(null); }}
                  className="rounded-xl"
                >
                  Nouvelle date
                </Button>
                <Button
                  onClick={() => navigate('/numerologia/daily')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                >
                  Nombres du quotidien
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NumerologiaCyclesPage;
